import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import Header from "@/components/app/Header";
import JobCard from "@/components/app/JobCard";
import JobDetailSheet from "@/components/app/JobDetailSheet";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  MagnifyingGlass, ArrowClockwise, Sparkle, Funnel, Warning, EnvelopeSimple,
  CaretLeft, CaretRight, LinkedinLogo, BookmarkSimple, CheckCircle, ChartBar,
  Lightning, Target, TrendUp, GlobeHemisphereWest, UserCircle, SlidersHorizontal,
} from "@phosphor-icons/react";
import { toast } from "sonner";

const CATEGORIES = [
  { value: "any", label: "All" },
  { value: "software-dev", label: "Software" },
  { value: "design", label: "Design" },
  { value: "product", label: "Product" },
  { value: "marketing", label: "Marketing" },
  { value: "data", label: "Data" },
  { value: "devops-sysadmin", label: "DevOps" },
  { value: "customer-support", label: "Support" },
];

const MIN_SCORES = [
  { value: "0", label: "Any" },
  { value: "30", label: "30+" },
  { value: "50", label: "50+" },
  { value: "70", label: "70+" },
  { value: "85", label: "85+" },
];

export default function Dashboard() {
  const { profile, refreshProfile } = useAuth();
  const nav = useNavigate();
  const [jobs, setJobs] = useState([]);
  const [query, setQuery] = useState("");
  const [activeQuery, setActiveQuery] = useState("");
  const [category, setCategory] = useState("");
  const [page, setPage] = useState(1);
  const [sources, setSources] = useState(["remotive"]);
  const [sourceAvail, setSourceAvail] = useState({});
  const [loading, setLoading] = useState(false);
  const [minScore, setMinScore] = useState(0);
  
  // New filters
  const [locationSearch, setLocationSearch] = useState("");
  const [activeLocation, setActiveLocation] = useState("");
  const [jobType, setJobType] = useState("");
  const [remotePref, setRemotePref] = useState("");
  const [experience, setExperience] = useState("");

  const [selected, setSelected] = useState(null);
  const [sheetOpen, setSheetOpen] = useState(false);
  const [stats, setStats] = useState(null);
  const [strongMatchCount, setStrongMatchCount] = useState(0);
  const [showFilters, setShowFilters] = useState(false);

  // Digest dialog
  const [digestOpen, setDigestOpen] = useState(false);
  const [digestPreview, setDigestPreview] = useState(null);
  const [digestSending, setDigestSending] = useState(false);

  useEffect(() => {
    (async () => {
      const data = await refreshProfile();
      if (!data?.profile) nav("/onboarding");
      loadStats();
      loadSources();
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadSources = async () => {
    try {
      const { data } = await api.get("/jobs/sources");
      setSourceAvail(data);
      // Include ALL available sources (free + paid with keys configured)
      // Exclude google_discovery (handled separately) and heavy duplicates
      const SKIP = new Set(["google_discovery", "jsearch", "linkedin", "indeed", "glassdoor", "google_jobs"]);
      const defaultSrcs = Object.keys(data).filter(
        k => data[k].available && !SKIP.has(k)
      );
      // Add google_discovery if available
      if (data.google_discovery?.available) defaultSrcs.push("google_discovery");
      if (defaultSrcs.length > 0) setSources(defaultSrcs);
    } catch {}
  };

  const loadStats = async () => {
    try {
      const { data } = await api.get("/dashboard");
      setStats(data.stats);
    } catch {}
  };

  const loadJobs = async (opts = {}) => {
    setLoading(true);
    try {
      const params = {};
      const q = opts.search ?? query;
      const cat = opts.category ?? category;
      const srcs = opts.sources ?? sources;
      const locSearch = opts.location ?? activeLocation;
      const p = opts.page ?? page;
      if (q) params.search = q;
      if (cat) params.category = cat;
      if (locSearch) params.location = locSearch;
      if (srcs?.length) params.sources = srcs.join(",");
      params.page = p;
      params.limit = 24;
      const { data } = await api.get("/jobs/discover", { params });
      setJobs(data.jobs || []);
      setActiveQuery(data.query || "");
      setStrongMatchCount(data.strong_matches || 0);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load matches");
    } finally {
      setLoading(false);
    }
  };

  const loadAll = (opts = {}) => {
    loadJobs(opts);
  };

  useEffect(() => { 
    if (profile && Object.keys(sourceAvail).length > 0) {
      loadAll();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profile, sourceAvail]);

  const toggleSource = (val) => {
    let next;
    if (sources.includes(val)) next = sources.filter((s) => s !== val);
    else next = [...sources, val];
    if (next.length === 0) next = ["remotive"];
    setSources(next);
    setPage(1);
    loadAll({ sources: next, page: 1 });
  };

  const goPage = (next) => {
    if (next < 1) return;
    setPage(next);
    loadJobs({ page: next });
  };

  const filtered = useMemo(() => {
    let res = jobs;
    if (minScore > 0) res = res.filter((j) => (j.analysis?.match_score ?? j.prescore ?? 0) >= minScore);
    if (jobType) res = res.filter(j => j.job_type && j.job_type.toLowerCase().includes(jobType.toLowerCase()));
    if (remotePref) {
      res = res.filter(j => {
        const isRemote = (j.candidate_required_location || j.location || "").toLowerCase().includes("remote") || (j.tags || []).join(" ").toLowerCase().includes("remote");
        return remotePref === "remote" ? isRemote : !isRemote;
      });
    }
    if (experience) {
      res = res.filter(j => (j.description || "").toLowerCase().includes(experience));
    }
    return res;
  }, [jobs, minScore, jobType, remotePref, experience]);


  const activeFilterCount = (category ? 1 : 0) + (activeLocation ? 1 : 0) + (minScore > 0 ? 1 : 0) + (jobType ? 1 : 0) + (remotePref ? 1 : 0) + (experience ? 1 : 0);
  
  const clearFilters = () => {
    setCategory("");
    setQuery("");
    setLocationSearch("");
    setActiveLocation("");
    setMinScore(0);
    setJobType("");
    setRemotePref("");
    setExperience("");
    setPage(1);
    loadAll({ search: "", category: "", location: "", page: 1 });
  };
  
  const applySearch = () => {
    setActiveLocation(locationSearch);
    setPage(1);
    loadAll({ page: 1, search: query, location: locationSearch });
  };

  const openJob = (job) => { setSelected(job); setSheetOpen(true); };

  const onAnalysed = (analysis) => {
    setJobs((prev) => prev.map((j) => (j.id === analysis.job_id ? { ...j, analysis } : j)));
    setSelected((prev) => (prev && prev.id === analysis.job_id ? { ...prev, analysis } : prev));
    loadStats();
  };

  const onJobStateChange = () => {
    loadAll({ page });
    loadStats();
  };

  const openDigest = async () => {
    setDigestOpen(true);
    setDigestPreview(null);
    try {
      const { data } = await api.get("/digest/preview", { params: { limit: 5 } });
      setDigestPreview(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load digest");
    }
  };

  const sendDigest = async () => {
    setDigestSending(true);
    try {
      const { data } = await api.post("/digest/send", { limit: 5 });
      toast.success(`Digest sent to ${data.to}`);
      setDigestOpen(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Send failed");
    } finally {
      setDigestSending(false);
    }
  };

  // Profile readiness score
  const profileReadiness = useMemo(() => {
    if (!profile) return 0;
    let score = 0;
    if (profile.full_name) score += 15;
    if (profile.skills?.length > 0) score += 25;
    if (profile.preferred_roles?.length > 0) score += 20;
    if (profile.experience_years) score += 15;
    if (profile.headline) score += 10;
    if (profile.preferred_locations?.length > 0) score += 10;
    if (profile.summary) score += 5;
    return Math.min(100, score);
  }, [profile]);

  return (
    <div className="min-h-screen bg-white">
      <Header />

      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-10 py-6 lg:py-8">

        {/* ═══ Hero Stats Bar ═══ */}
        <div className="mb-8">
          <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
            <div>
              <span className="tick-label">Dashboard</span>
              <h1 className="mt-2 font-display font-black text-3xl sm:text-4xl lg:text-5xl tracking-tighter">
                {profile?.full_name ? `Hi, ${profile.full_name.split(" ")[0]}.` : "Welcome back."}
              </h1>
              <p className="mt-1.5 text-ink-soft text-sm max-w-lg">
                {activeQuery ? (
                  <>Showing jobs for <span className="font-semibold text-ink">"{activeQuery}"</span></>
                ) : "Live remote roles, ranked against your profile."}
              </p>
            </div>

            <div className="flex flex-wrap items-center gap-3">
              <Button onClick={openDigest} className="rounded-xl bg-ink hover:bg-brand text-white uppercase tracking-wider text-xs h-10 px-4" data-testid="digest-btn">
                <EnvelopeSimple size={14} weight="bold" className="mr-2" />Digest
              </Button>
            </div>
          </div>

          {/* Stats Cards */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <div className="border border-slate-200/60 bg-white/50 backdrop-blur-sm shadow-sm p-4 hover:border-slate-200 transition-colors">
              <div className="flex items-center gap-2 mb-2">
                <div className="w-6 h-6 bg-brand/10 flex items-center justify-center">
                  <Target size={14} weight="bold" className="text-brand" />
                </div>
                <span className="tick-label">Matches</span>
              </div>
              <div className="font-mono font-black text-2xl">{strongMatchCount}</div>
              <div className="text-[11px] text-ink-mute mt-0.5">of {jobs.length} total</div>
            </div>

            <div className="border border-slate-200/60 bg-white/50 backdrop-blur-sm shadow-sm p-4 hover:border-slate-200 transition-colors">
              <div className="flex items-center gap-2 mb-2">
                <div className="w-6 h-6 bg-emerald-50 flex items-center justify-center">
                  <BookmarkSimple size={14} weight="bold" className="text-emerald-600" />
                </div>
                <span className="tick-label">Saved</span>
              </div>
              <div className="font-mono font-black text-2xl text-emerald-600">{stats?.saved || 0}</div>
              <div className="text-[11px] text-ink-mute mt-0.5">bookmarked</div>
            </div>

            <div className="border border-slate-200/60 bg-white/50 backdrop-blur-sm shadow-sm p-4 hover:border-slate-200 transition-colors">
              <div className="flex items-center gap-2 mb-2">
                <div className="w-6 h-6 bg-blue-50 flex items-center justify-center">
                  <CheckCircle size={14} weight="bold" className="text-blue-600" />
                </div>
                <span className="tick-label">Applied</span>
              </div>
              <div className="font-mono font-black text-2xl text-blue-600">{stats?.applied || 0}</div>
              <div className="text-[11px] text-ink-mute mt-0.5">submitted</div>
            </div>

            <div className="border border-slate-200/60 bg-white/50 backdrop-blur-sm shadow-sm p-4 hover:border-slate-200 transition-colors">
              <div className="flex items-center gap-2 mb-2">
                <div className="w-6 h-6 bg-amber-50 flex items-center justify-center">
                  <UserCircle size={14} weight="bold" className="text-amber-600" />
                </div>
                <span className="tick-label">Profile</span>
              </div>
              <div className="font-mono font-black text-2xl text-amber-600">{profileReadiness}%</div>
              <div className="h-1 w-full bg-slate-50 mt-2">
                <div className="h-full bg-amber-500 transition-all duration-500" style={{ width: `${profileReadiness}%` }} />
              </div>
            </div>
          </div>
        </div>

        {/* ═══ Search & Filters ═══ */}
        <div className="border border-slate-200/60 bg-white shadow-sm mb-8">
          {/* Active Chips Row (if any) */}
          {activeFilterCount > 0 && (
            <div className="bg-slate-50 border-b border-border px-4 py-2 flex items-center gap-2 overflow-x-auto whitespace-nowrap">
              <span className="text-[10px] uppercase font-bold text-ink-mute tracking-wider mr-1 flex-shrink-0">Filters:</span>
              {category && <Badge variant="outline" className="bg-white text-xs border-slate-200 rounded-lg font-normal">{CATEGORIES.find(c => c.value === category)?.label || category}</Badge>}
              {activeLocation && <Badge variant="outline" className="bg-white text-xs border-slate-200 rounded-lg font-normal">Loc: {activeLocation}</Badge>}
              {minScore > 0 && <Badge variant="outline" className="bg-white text-xs border-slate-200 rounded-lg font-normal">Score: {minScore}+</Badge>}
              {jobType && <Badge variant="outline" className="bg-white text-xs border-slate-200 rounded-lg font-normal">Type: {jobType}</Badge>}
              {remotePref && <Badge variant="outline" className="bg-white text-xs border-slate-200 rounded-lg font-normal">{remotePref === "remote" ? "Remote" : "Onsite/Hybrid"}</Badge>}
              {experience && <Badge variant="outline" className="bg-white text-xs border-slate-200 rounded-lg font-normal">Exp: {experience}</Badge>}
              <button onClick={clearFilters} className="ml-auto text-xs text-brand font-semibold hover:underline">Clear all</button>
            </div>
          )}

          {/* Search row */}
          <div className="p-4 flex flex-col md:flex-row gap-3">
            <div className="flex-1 relative">
              <MagnifyingGlass size={16} weight="bold" className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-mute" />
              <Input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && applySearch()}
                placeholder="Job title, keywords, or company…"
                className="rounded-xl pl-10 h-11 border-border focus-visible:ring-brand focus-visible:ring-1 focus-visible:ring-offset-0"
              />
            </div>
            <div className="w-full md:w-64 relative">
              <Input
                value={locationSearch}
                onChange={(e) => setLocationSearch(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && applySearch()}
                placeholder="Location (e.g. India, Remote)…"
                className="rounded-xl h-11 border-border focus-visible:ring-brand focus-visible:ring-1 focus-visible:ring-offset-0"
              />
            </div>
            <div className="flex gap-2">
              <Button onClick={applySearch} disabled={loading} className="rounded-xl bg-brand hover:bg-brand-hover text-white uppercase tracking-wider text-xs h-11 px-5 w-full md:w-auto">
                {loading ? "…" : "Search"}
              </Button>
              <Button onClick={() => setShowFilters(!showFilters)} variant="outline" className={`rounded-xl border-slate-200 text-ink hover:bg-ink hover:text-white uppercase tracking-wider text-xs h-11 px-3 ${showFilters ? 'bg-ink text-white' : ''}`}>
                <Funnel size={14} weight="bold" />
              </Button>
            </div>
          </div>

          {/* Expandable filters panel */}
          {showFilters && (
            <div className="border-t border-border p-4 sm:p-6 bg-slate-50/50 animate-fade-up">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                
                {/* Categories */}
                <div>
                  <span className="tick-label mb-2 block">Role Category</span>
                  <Select 
                    value={category || "any"} 
                    onValueChange={(val) => { 
                      const next = val === "any" ? "" : val;
                      setCategory(next); 
                      setPage(1); 
                      loadAll({ category: next, page: 1 }); 
                    }}
                  >
                    <SelectTrigger className="w-full h-10 px-3 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-1 focus:ring-brand bg-white">
                      <SelectValue placeholder="Category" />
                    </SelectTrigger>
                    <SelectContent>
                      {CATEGORIES.map((c) => (
                        <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Score & Experience */}
                <div className="flex gap-3">
                  <div className="w-1/2">
                    <span className="tick-label mb-2 block">Min Score</span>
                    <Select 
                      value={String(minScore)} 
                      onValueChange={(val) => setMinScore(Number(val))}
                    >
                      <SelectTrigger className="w-full h-10 px-3 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-1 focus:ring-brand bg-white">
                        <SelectValue placeholder="Any" />
                      </SelectTrigger>
                      <SelectContent>
                        {MIN_SCORES.map((s) => (
                          <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="w-1/2">
                    <span className="tick-label mb-2 block">Experience</span>
                    <Select 
                      value={experience || "any"} 
                      onValueChange={(val) => setExperience(val === "any" ? "" : val)}
                    >
                      <SelectTrigger className="w-full h-10 px-3 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-1 focus:ring-brand bg-white">
                        <SelectValue placeholder="Any" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="any">Any</SelectItem>
                        <SelectItem value="junior">Junior</SelectItem>
                        <SelectItem value="mid">Mid-level</SelectItem>
                        <SelectItem value="senior">Senior</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {/* Job Type & Remote */}
                <div className="flex gap-3">
                  <div className="w-1/2">
                    <span className="tick-label mb-2 block">Job Type</span>
                    <Select 
                      value={jobType || "any"} 
                      onValueChange={(val) => setJobType(val === "any" ? "" : val)}
                    >
                      <SelectTrigger className="w-full h-10 px-3 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-1 focus:ring-brand bg-white">
                        <SelectValue placeholder="Any" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="any">Any</SelectItem>
                        <SelectItem value="full_time">Full-time</SelectItem>
                        <SelectItem value="contract">Contract</SelectItem>
                        <SelectItem value="freelance">Freelance</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="w-1/2">
                    <span className="tick-label mb-2 block">Workspace</span>
                    <Select 
                      value={remotePref || "any"} 
                      onValueChange={(val) => setRemotePref(val === "any" ? "" : val)}
                    >
                      <SelectTrigger className="w-full h-10 px-3 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-1 focus:ring-brand bg-white">
                        <SelectValue placeholder="Any" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="any">Any</SelectItem>
                        <SelectItem value="remote">Remote Only</SelectItem>
                        <SelectItem value="onsite">Onsite/Hybrid</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>

              {/* Sources */}
              <div className="mt-6 pt-4 border-t border-slate-200/60">
                <span className="tick-label mb-3 block">Marketplace Sources ({Object.keys(sourceAvail).length})</span>
                <div className="flex flex-wrap gap-2">
                  {Object.entries(sourceAvail).map(([key, info]) => {
                    const enabled = info.available;
                    const active = sources.includes(key);
                    const isLinkedIn = key === "linkedin";
                    const isGoogle = key === "google_discovery";
                    return (
                      <button
                        key={key}
                        onClick={() => enabled && toggleSource(key)}
                        disabled={!enabled}
                        className={`text-[11px] uppercase tracking-wider font-semibold px-3 h-8 border rounded-lg transition-colors inline-flex items-center gap-1.5 ${
                          active ? "bg-ink text-white border-ink shadow-sm" : "bg-white text-ink-soft border-slate-200 hover:border-slate-300 hover:text-ink"
                        } ${!enabled ? "opacity-40 cursor-not-allowed" : ""}`}
                        title={enabled ? "" : info.reason}
                      >
                        {isLinkedIn && <LinkedinLogo size={12} weight="bold" />}
                        {isGoogle && <GlobeHemisphereWest size={12} weight="bold" />}
                        {info.label}
                        {!enabled && <span className="ml-1 text-[9px] font-mono">KEY</span>}
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* ═══ AI Matches Section ═══ */}
        <div className="mb-6 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-2">
              <Lightning size={16} weight="fill" className="text-brand" />
              <span className="font-display font-bold text-lg tracking-tight">Your Matches</span>
            </div>
            <Badge variant="outline" className="rounded-xl border-brand text-brand text-[10px] uppercase tracking-wider px-2 py-0">
              {strongMatchCount} strong
            </Badge>
          </div>
          <div className="inline-flex items-center gap-2 text-xs text-ink-soft">
            <Sparkle size={12} weight="fill" className="text-brand" />
            Click any job for AI draft
          </div>
        </div>

        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4 mb-10">
            {Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className="border border-slate-200/60 bg-white/50 backdrop-blur-sm shadow-sm p-5 h-64 animate-pulse bg-slate-50/50">
                <div className="h-4 bg-slate-50 rounded w-1/4 mb-3" />
                <div className="h-6 bg-slate-50 rounded w-3/4 mb-2" />
                <div className="h-4 bg-slate-50 rounded w-1/2 mb-4" />
                <div className="h-3 bg-slate-50 rounded w-full mb-2" />
                <div className="h-3 bg-slate-50 rounded w-5/6" />
              </div>
            ))}
          </div>
        ) : filtered.length === 0 ? (
          <div className="border border-slate-200/60 bg-white/50 backdrop-blur-sm shadow-sm p-12 text-center mb-10" data-testid="empty-state">
            <Warning size={32} weight="duotone" className="mx-auto text-ink-mute mb-4" />
            <h3 className="font-display font-bold text-xl mb-2">No strong matches yet</h3>
            <p className="text-sm text-ink-soft max-w-md mx-auto mb-6">
              {minScore > 0
                ? `No jobs above ${minScore} score. Try lowering the threshold or broadening your search.`
                : "Try widening your search, changing sources, or updating your profile with more skills."
              }
            </p>
            <div className="flex flex-wrap justify-center gap-3">
              {minScore > 0 && (
                <Button onClick={() => setMinScore(0)} variant="outline" className="rounded-xl border-slate-200 text-ink hover:bg-ink hover:text-white uppercase tracking-wider text-xs h-10 px-4">
                  Show all scores
                </Button>
              )}
              <Button onClick={() => nav("/profile")} variant="outline" className="rounded-xl border-brand text-brand hover:bg-brand hover:text-white uppercase tracking-wider text-xs h-10 px-4">
                Update profile
              </Button>
            </div>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4 mb-6" data-testid="matches-grid">
              {filtered.map((j, i) => (
                <div key={`match-${j.id}`} className="animate-fade-up" style={{ animationDelay: `${Math.min(i * 30, 300)}ms` }}>
                  <JobCard job={j} onOpen={openJob} onStateChange={onJobStateChange} />
                </div>
              ))}
            </div>
            
            {/* Pagination */}
            <div className="mb-16 flex items-center justify-center gap-1.5">
              <Button 
                onClick={() => goPage(page - 1)} 
                disabled={page <= 1} 
                variant="ghost" 
                className="rounded-lg text-ink-soft hover:text-ink hover:bg-slate-50 h-8 px-3 text-xs disabled:opacity-40" 
                data-testid="prev-page"
              >
                <CaretLeft size={12} weight="bold" className="mr-1" /> Prev
              </Button>
              
              {Array.from({ length: 10 }, (_, i) => i + 1)
                .filter(p => p <= page || (p === page + 1 && jobs.length === 24))
                .map((p) => (
                  <button
                    key={p}
                    onClick={() => goPage(p)}
                    className={`w-8 h-8 flex items-center justify-center rounded-lg text-[11px] font-bold transition-colors ${
                      page === p 
                        ? "bg-ink text-white" 
                        : "text-ink-soft hover:bg-slate-50 hover:text-ink"
                    }`}
                  >
                    {p}
                  </button>
                ))}

              <Button 
                onClick={() => goPage(page + 1)} 
                disabled={jobs.length < 24 || page >= 10}
                variant="ghost" 
                className="rounded-lg text-ink-soft hover:text-ink hover:bg-slate-50 h-8 px-3 text-xs disabled:opacity-40" 
                data-testid="next-page"
              >
                Next <CaretRight size={12} weight="bold" className="ml-1" />
              </Button>
            </div>
          </>
        )}

      </div>

      <JobDetailSheet open={sheetOpen} job={selected} onOpenChange={setSheetOpen} onAnalysed={onAnalysed} onStateChange={onJobStateChange} />

      {/* Digest dialog */}
      <Dialog open={digestOpen} onOpenChange={setDigestOpen}>
        <DialogContent className="rounded-xl border-slate-200 max-w-lg" data-testid="digest-dialog">
          <DialogHeader>
            <DialogTitle className="font-display font-black text-2xl tracking-tight">Weekly digest</DialogTitle>
          </DialogHeader>
          {!digestPreview ? (
            <div className="py-8 text-center text-sm text-ink-soft">Loading preview…</div>
          ) : (
            <div className="space-y-4">
              <div className="border border-slate-200/60 bg-white/50 backdrop-blur-sm shadow-sm p-4">
                <span className="tick-label">Recipient</span>
                <div className="font-mono text-sm mt-1">{digestPreview.to}</div>
              </div>
              <div>
                <span className="tick-label">Top {digestPreview.matches?.length || 0} matches</span>
                <div className="mt-2 divide-y divide-border border border-slate-200/60 bg-white/50 backdrop-blur-sm shadow-sm">
                  {(digestPreview.matches || []).length === 0 && (
                    <div className="p-4 text-sm text-ink-soft">No analysed jobs yet — open a job and run "Analyse with AI" first.</div>
                  )}
                  {(digestPreview.matches || []).map((m) => (
                    <div key={m.job_id} className="p-3 flex items-center justify-between gap-3">
                      <div className="min-w-0">
                        <div className="font-display font-bold text-sm truncate">{m.job?.title}</div>
                        <div className="text-xs text-ink-soft truncate">{m.job?.company_name}</div>
                      </div>
                      <div className="font-mono font-black text-xl text-brand">{m.match_score}</div>
                    </div>
                  ))}
                </div>
              </div>
              {!digestPreview.email_configured && (
                <div className="border border-amber-300 bg-amber-50 p-3 text-xs text-amber-800">
                  Email not configured. Add <span className="font-mono">RESEND_API_KEY</span> and <span className="font-mono">RESEND_FROM_EMAIL</span> to <span className="font-mono">/app/backend/.env</span> to enable sending.
                </div>
              )}
            </div>
          )}
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setDigestOpen(false)} className="rounded-xl border-slate-200 text-ink hover:bg-ink hover:text-white uppercase tracking-wider text-xs h-10 px-4" data-testid="digest-close">
              Close
            </Button>
            <Button
              onClick={sendDigest}
              disabled={digestSending || !digestPreview?.email_configured || !digestPreview?.matches?.length}
              className="rounded-xl bg-brand hover:bg-brand-hover text-white uppercase tracking-wider text-xs h-10 px-5"
              data-testid="digest-send"
            >
              {digestSending ? "Sending…" : "Send now"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
